<template>
  <div class="min-h-screen p-4">
    <div v-if="loading">
      <LoadingIndicator />
    </div>
    <div v-else>
      <div class="flex flex-col gap-3">

        <!-- Category Definitions -->
        <div class="bg-blue-50 rounded border border-blue-200">
          <ComponentHeader title="Loyalty Category Definitions" :heading-level="3" />
          <div class="p-3 text-xs text-gray-700 space-y-1">
            <div><span class="font-semibold text-blue-600">New Patients:</span> Beneficiaries attributed for the first time in 2025 (FIRST_SEEN = 2025)</div>
            <div><span class="font-semibold text-yellow-600">Somewhat Loyal:</span> Beneficiaries attributed in 2 of the 4 years (2022-2025)</div>
            <div><span class="font-semibold text-orange-600">Loyal:</span> Beneficiaries attributed in 3 of the 4 years (2022-2025)</div>
            <div><span class="font-semibold text-green-600">Very Loyal:</span> Beneficiaries attributed in all 4 years (2022-2025)</div>
          </div>
        </div>

        <!-- Loyalty Breakdown Pie Chart and Comparison Chart -->
        <div class="bg-white rounded border border-gray-200">
          <ComponentHeader title="2025 Attribution Loyalty Breakdown" :heading-level="3" />
          <div class="p-3">
          <div v-if="loyaltyPieData.labels.length > 0" class="flex flex-row gap-4">
            <div class="flex-1">
              <PieChart
                :chart_data="loyaltyPieData"
                :chart_config="loyaltyPieConfig"
                :override-height="'h-[400px]'"
                @slice-click="handleSliceClick"
              />
            </div>
            <div class="flex-1">
              <ChartV4
                :chart_data="loyaltyComparisonChartData"
                :chart_config="loyaltyComparisonChartConfig"
                :override-height="'h-[400px]'"
              />
            </div>
          </div>
          <div v-else class="text-xs text-gray-500">No 2025 attribution data available</div>
          </div>
        </div>

        <!-- Portfolio-Level Statistics Table -->
        <div class="bg-gray-50 rounded border border-gray-200" v-if="loyaltyStats.length > 0">
          <ComponentHeader title="Portfolio-Level Summary" :heading-level="3" />
          <div class="p-3">
          <div class="overflow-x-auto">
            <table class="min-w-full bg-white border border-gray-300 text-xs">
              <thead class="bg-gray-100">
                <tr>
                  <th class="px-2 py-1 border text-left">Category</th>
                  <th class="px-2 py-1 border text-right">Count</th>
                  <th class="px-2 py-1 border text-right">Avg Risk Score</th>
                  <th class="px-2 py-1 border text-right">Avg Annual Expense</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="stat in loyaltyStats" :key="stat.category">
                  <td class="px-2 py-1 border font-medium">{{ stat.category }}</td>
                  <td class="px-2 py-1 border text-right">{{ stat.count.toLocaleString() }}</td>
                  <td class="px-2 py-1 border text-right">{{ stat.avgRiskScore }}</td>
                  <td class="px-2 py-1 border text-right">{{ stat.avgExpense }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          </div>
        </div>

        <!-- Entity-Level Table -->
        <div class="bg-gray-50 rounded border border-gray-200" v-if="entityStats.length > 0">
          <ComponentHeader :title="`Entity-Level Breakdown (${entityStats.length} entities)`" :heading-level="3">
            <div class="text-xs text-gray-600">
              <span class="bg-yellow-100 px-2 py-1 rounded border border-yellow-300">25-50% New</span>
              <span class="bg-orange-200 px-2 py-1 rounded border border-orange-300 ml-2">&gt;50% New</span>
              <span class="font-bold ml-3">Bold = Avg Exp &gt; $25k</span>
            </div>
          </ComponentHeader>
          <div class="p-3">
          <div class="overflow-x-auto">
            <table class="min-w-full bg-white border border-gray-300 text-xs">
              <thead class="bg-gray-100">
                <tr>
                  <th rowspan="2" class="px-2 py-1 border text-left align-bottom">Entity Type</th>
                  <th rowspan="2" class="px-2 py-1 border text-left align-bottom">Entity ID</th>
                  <th rowspan="2" class="px-2 py-1 border text-left align-bottom">Entity Name</th>
                  <th colspan="3" class="px-2 py-1 border text-center bg-blue-100">New Patients</th>
                  <th colspan="3" class="px-2 py-1 border text-center bg-yellow-100">Somewhat Loyal</th>
                  <th colspan="3" class="px-2 py-1 border text-center bg-orange-100">Loyal</th>
                  <th colspan="3" class="px-2 py-1 border text-center bg-green-100">Very Loyal</th>
                  <th rowspan="2" class="px-2 py-1 border text-center align-bottom">Actions</th>
                </tr>
                <tr>
                  <!-- New Patients -->
                  <th class="px-2 py-1 border text-right bg-blue-50">Count</th>
                  <th class="px-2 py-1 border text-right bg-blue-50">Avg Risk</th>
                  <th class="px-2 py-1 border text-right bg-blue-50">Avg Exp</th>
                  <!-- Somewhat Loyal -->
                  <th class="px-2 py-1 border text-right bg-yellow-50">Count</th>
                  <th class="px-2 py-1 border text-right bg-yellow-50">Avg Risk</th>
                  <th class="px-2 py-1 border text-right bg-yellow-50">Avg Exp</th>
                  <!-- Loyal -->
                  <th class="px-2 py-1 border text-right bg-orange-50">Count</th>
                  <th class="px-2 py-1 border text-right bg-orange-50">Avg Risk</th>
                  <th class="px-2 py-1 border text-right bg-orange-50">Avg Exp</th>
                  <!-- Very Loyal -->
                  <th class="px-2 py-1 border text-right bg-green-50">Count</th>
                  <th class="px-2 py-1 border text-right bg-green-50">Avg Risk</th>
                  <th class="px-2 py-1 border text-right bg-green-50">Avg Exp</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="entity in entityStats"
                  :key="entity.entityId"
                  :class="{
                    'bg-yellow-100': entity.newPatientPercentage > 25 && entity.newPatientPercentage <= 50,
                    'bg-orange-200': entity.newPatientPercentage > 50
                  }"
                >
                  <td class="px-2 py-1 border">{{ entity.entityType }}</td>
                  <td class="px-2 py-1 border font-mono">{{ entity.entityId }}</td>
                  <td class="sensitive px-2 py-1 border">{{ entity.entityName || 'N/A' }}</td>

                  <!-- New Patients -->
                  <td class="px-2 py-1 border text-right">{{ entity.newPatients.count }}</td>
                  <td class="px-2 py-1 border text-right">{{ entity.newPatients.avgRisk }}</td>
                  <td class="px-2 py-1 border text-right" :class="{ 'font-bold': entity.newPatients.avgExpValue > 25000 }">{{ entity.newPatients.avgExp }}</td>

                  <!-- Somewhat Loyal -->
                  <td class="px-2 py-1 border text-right">{{ entity.somewhatLoyal.count }}</td>
                  <td class="px-2 py-1 border text-right">{{ entity.somewhatLoyal.avgRisk }}</td>
                  <td class="px-2 py-1 border text-right" :class="{ 'font-bold': entity.somewhatLoyal.avgExpValue > 25000 }">{{ entity.somewhatLoyal.avgExp }}</td>

                  <!-- Loyal -->
                  <td class="px-2 py-1 border text-right">{{ entity.loyal.count }}</td>
                  <td class="px-2 py-1 border text-right">{{ entity.loyal.avgRisk }}</td>
                  <td class="px-2 py-1 border text-right" :class="{ 'font-bold': entity.loyal.avgExpValue > 25000 }">{{ entity.loyal.avgExp }}</td>

                  <!-- Very Loyal -->
                  <td class="px-2 py-1 border text-right">{{ entity.veryLoyal.count }}</td>
                  <td class="px-2 py-1 border text-right">{{ entity.veryLoyal.avgRisk }}</td>
                  <td class="px-2 py-1 border text-right" :class="{ 'font-bold': entity.veryLoyal.avgExpValue > 25000 }">{{ entity.veryLoyal.avgExp }}</td>

                  <!-- Actions -->
                  <td class="px-2 py-1 border text-center">
                    <button
                      class="px-2 py-0.5 text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 rounded"
                      @click="handleAddTag(entity)"
                    >
                      Add Tag
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          </div>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import PieChart from '@/components/widgetcomponents/Viz_Elements/Pie_Chart.vue';
import ChartV4 from '@/components/widgetcomponents/Viz_Elements/Chart_V4.vue';
import ComponentHeader from '@/components/widgetcomponents/library/ComponentHeader.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';

export default {
  name: 'Attribution_Flow',
  components: {
    PieChart,
    ChartV4,
    ComponentHeader,
    LoadingIndicator
  },
  props: {
    entityId: {
      type: Number,
      required: false,
      default: null
    }
  },
  data() {
    return {
      loading: false,
      attributionData: []
    };
  },
  computed: {
    // Filter for 2025 attributed beneficiaries and categorize
    categorizedData() {
      // Log first row to debug
      if (this.attributionData.length > 0) {
        console.log('Sample data row:', this.attributionData[0]);
        console.log('last_seen type:', typeof this.attributionData[0].last_seen);
      }

      // Convert to numbers for comparison (handles both string and number types)
      const current2025 = this.attributionData.filter(row => Number(row.last_seen) === 2025);

      console.log(`Filtered ${current2025.length} rows with last_seen = 2025 out of ${this.attributionData.length} total`);

      const categories = {
        'New Patients': [],
        'Very Loyal': [],
        'Loyal': [],
        'Somewhat Loyal': []
      };

      current2025.forEach(row => {
        const firstSeen = Number(row.first_seen);
        const lastSeen = Number(row.last_seen);
        const numTimesAttributed = lastSeen - firstSeen + 1;

        if (firstSeen === 2025) {
          categories['New Patients'].push(row);
        } else if (numTimesAttributed === 4) {
          categories['Very Loyal'].push(row);
        } else if (numTimesAttributed === 3) {
          categories['Loyal'].push(row);
        } else if (numTimesAttributed === 2) {
          categories['Somewhat Loyal'].push(row);
        }
      });

      console.log('Category counts:', {
        'New Patients': categories['New Patients'].length,
        'Very Loyal': categories['Very Loyal'].length,
        'Loyal': categories['Loyal'].length,
        'Somewhat Loyal': categories['Somewhat Loyal'].length
      });

      return categories;
    },

    // Generate pie chart data
    loyaltyPieData() {
      const labels = [];
      const values = [];
      const colors = [];

      const colorMap = {
        'New Patients': '#3b82f6',      // blue
        'Somewhat Loyal': '#fbbf24',    // yellow
        'Loyal': '#f97316',             // orange
        'Very Loyal': '#22c55e'         // green
      };

      const orderedCategories = ['New Patients', 'Somewhat Loyal', 'Loyal', 'Very Loyal'];

      orderedCategories.forEach(category => {
        const count = this.categorizedData[category].length;
        if (count > 0) {
          labels.push(category);
          values.push(count);
          colors.push(colorMap[category]);
        }
      });

      return { labels, values, colors };
    },

    // Pie chart configuration
    loyaltyPieConfig() {
      return {
        margin: { top: 80, right: 150, bottom: 80, left: 150 },
        innerRadius: 0,
        outerRadius: 120,
        padAngle: 0.02,
        cornerRadius: 3,
        labelDisplay: {
          showLabels: true,
          showValues: true,
          minAngleForLabel: 0.05,
          minPercentageForLabel: 2,
          labelOffset: 30
        },
        legend: {
          display: false,
          position: 'right',
          spacing: 25
        },
        formatting: {
          percentage: false,
          sig_figs: 0
        },
        title: '',
        subtitle: '',
        tooltip: {
          enabled: true
        }
      };
    },

    // Calculate statistics for each category
    loyaltyStats() {
      const stats = [];
      const orderedCategories = ['New Patients', 'Somewhat Loyal', 'Loyal', 'Very Loyal'];

      orderedCategories.forEach(category => {
        const categoryData = this.categorizedData[category];

        if (categoryData.length > 0) {
          // Calculate avg risk score (excluding nulls)
          const riskScores = categoryData
            .map(row => Number(row.risk_score))
            .filter(score => !isNaN(score) && isFinite(score));

          const avgRiskScore = riskScores.length > 0
            ? (riskScores.reduce((sum, val) => sum + val, 0) / riskScores.length).toFixed(2)
            : 'N/A';

          // Calculate avg annual expense (excluding nulls)
          const expenses = categoryData
            .map(row => Number(row.tot_cap_ann_exp))
            .filter(exp => !isNaN(exp) && isFinite(exp));

          const avgExpense = expenses.length > 0
            ? '$' + (expenses.reduce((sum, val) => sum + val, 0) / expenses.length).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
            : 'N/A';

          stats.push({
            category,
            count: categoryData.length,
            avgRiskScore,
            avgExpense
          });
        }
      });

      return stats;
    },

    // Chart data for comparing avg risk score and avg annual expense by loyalty category
    loyaltyComparisonChartData() {
      const orderedCategories = ['New Patients', 'Somewhat Loyal', 'Loyal', 'Very Loyal'];

      const labels = [];
      const riskScores = [];
      const expenses = [];

      orderedCategories.forEach(category => {
        const categoryData = this.categorizedData[category];

        if (categoryData.length > 0) {
          labels.push(category);

          // Calculate avg risk score
          const riskScoresArray = categoryData
            .map(row => Number(row.risk_score))
            .filter(score => !isNaN(score) && isFinite(score));

          const avgRisk = riskScoresArray.length > 0
            ? riskScoresArray.reduce((sum, val) => sum + val, 0) / riskScoresArray.length
            : 0;
          riskScores.push(avgRisk);

          // Calculate avg annual expense
          const expensesArray = categoryData
            .map(row => Number(row.tot_cap_ann_exp))
            .filter(exp => !isNaN(exp) && isFinite(exp));

          const avgExp = expensesArray.length > 0
            ? expensesArray.reduce((sum, val) => sum + val, 0) / expensesArray.length
            : 0;
          expenses.push(avgExp);
        }
      });

      return {
        labels,
        series: [
          {
            values: riskScores,
            config: {
              label: 'Avg Risk Score',
              backgroundColor: '#60a5fa',  // lighter blue for risk
              type: 'bar',
              yAxis: 'left',
              offset: 0
            }
          },
          {
            values: expenses,
            config: {
              label: 'Avg Annual Expense',
              backgroundColor: '#2563eb',  // darker blue for expense
              type: 'bar',
              yAxis: 'right',
              offset: 1
            }
          }
        ]
      };
    },

    // Config for the comparison chart
    loyaltyComparisonChartConfig() {
      return {
        chartType: 'vertical_bar',
        margin: { top: 60, right: 80, bottom: 80, left: 60 },
        maxBarWidth: 60,
        barGap: 4,
        xAxis: {
          labelRotation: -25,
          tickFormat: null,
          fontSize: 11
        },
        yAxes: [
          {
            position: 'left',
            tickFormat: (d) => d.toFixed(1),
            fontSize: 10
          },
          {
            position: 'right',
            tickFormat: (d) => '$' + (d / 1000).toFixed(0) + 'k',
            fontSize: 10
          }
        ],
        legend: {
          display: true,
          position: 'subtitle',
          spacing: 15
        },
        labelDisplayConfig: {
          maxLabels: 10,
          minLabelSpacing: 5,
          labelFontSize: 10
        },
        formatting: {
          percentage: false,
          sig_figs: 1
        },
        title: 'Avg Risk Score vs Avg Annual Expense',
        subtitle: ''
      };
    },

    // Calculate entity-level statistics
    entityStats() {
      const entityMap = {};

      // Helper function to calculate stats for a category
      const calculateCategoryStats = (categoryData) => {
        const riskScores = categoryData
          .map(row => Number(row.risk_score))
          .filter(score => !isNaN(score) && isFinite(score));

        const expenses = categoryData
          .map(row => Number(row.tot_cap_ann_exp))
          .filter(exp => !isNaN(exp) && isFinite(exp));

        const avgExpValue = expenses.length > 0
          ? expenses.reduce((sum, val) => sum + val, 0) / expenses.length
          : 0;

        return {
          count: categoryData.length,
          avgRisk: riskScores.length > 0
            ? (riskScores.reduce((sum, val) => sum + val, 0) / riskScores.length).toFixed(2)
            : '-',
          avgExp: avgExpValue > 0
            ? '$' + avgExpValue.toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
            : '-',
          avgExpValue: avgExpValue
        };
      };

      // Group all beneficiaries by entity
      Object.keys(this.categorizedData).forEach(category => {
        this.categorizedData[category].forEach(row => {
          // Determine entity key (prefer TIN over CCN)
          const entityId = row.tin || row.ccn;
          const entityType = row.tin ? 'TIN' : row.ccn ? 'CCN' : null;
          const entityName = row.entity_name;

          if (!entityId) return;

          // Initialize entity if not exists
          if (!entityMap[entityId]) {
            entityMap[entityId] = {
              entityId,
              entityType,
              entityName,
              categories: {
                'New Patients': [],
                'Somewhat Loyal': [],
                'Loyal': [],
                'Very Loyal': []
              }
            };
          }

          // Add beneficiary to the appropriate category for this entity
          entityMap[entityId].categories[category].push(row);
        });
      });

      // Convert to array and calculate stats for each entity
      const entities = Object.values(entityMap).map(entity => {
        const newPatients = calculateCategoryStats(entity.categories['New Patients']);
        const somewhatLoyal = calculateCategoryStats(entity.categories['Somewhat Loyal']);
        const loyal = calculateCategoryStats(entity.categories['Loyal']);
        const veryLoyal = calculateCategoryStats(entity.categories['Very Loyal']);

        // Calculate total patients and new patient percentage
        const totalPatients = newPatients.count + somewhatLoyal.count + loyal.count + veryLoyal.count;
        const newPatientPercentage = totalPatients > 0 ? (newPatients.count / totalPatients) * 100 : 0;

        return {
          entityId: entity.entityId,
          entityType: entity.entityType,
          entityName: entity.entityName,
          newPatients,
          somewhatLoyal,
          loyal,
          veryLoyal,
          newPatientPercentage
        };
      });

      // Sort by total count (descending)
      return entities.sort((a, b) => {
        const totalA = a.newPatients.count + a.somewhatLoyal.count + a.loyal.count + a.veryLoyal.count;
        const totalB = b.newPatients.count + b.somewhatLoyal.count + b.loyal.count + b.veryLoyal.count;
        return totalB - totalA;
      });
    }
  },
  methods: {
    async fetchAttributionFlow() {
      this.loading = true;
      try {
        const response = await axios.post('/api/portfolio_management/attribution-flow/', {});
        this.attributionData = response.data;
        console.log('Attribution Flow Data:', this.attributionData);
      } catch (error) {
        console.error('Error fetching attribution flow:', error);
      } finally {
        this.loading = false;
      }
    },
    handleSliceClick(data) {
      console.log('Clicked slice:', data);
      // Add any click handling logic here
    },
    handleAddTag(entity) {
      console.log('Add tag for entity:', entity);
      // Placeholder for tag functionality
    }
  },
  mounted() {
    this.fetchAttributionFlow();
  }
};
</script>

